/**
 * product-page.js
 * Premium Shopify Product Page — Vanilla JS
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────
     DOM helpers
  ───────────────────────────────────────── */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

  /* ─────────────────────────────────────────
     Gallery
  ───────────────────────────────────────── */
  function initGallery() {
    const mainImg = $('#pp-main-img');
    const thumbBtns = $$('.pp-thumb-btn');
    if (!mainImg || !thumbBtns.length) return;

    function setActive(btn) {
      thumbBtns.forEach(t => t.classList.remove('is-active'));
      btn.classList.add('is-active');
      const src    = btn.dataset.imgSrc;
      const srcset = btn.dataset.imgSrcset || '';
      const alt    = btn.dataset.imgAlt || '';
      mainImg.style.opacity = '0';
      setTimeout(() => {
        mainImg.src = src;
        if (srcset) mainImg.srcset = srcset;
        mainImg.alt = alt;
        mainImg.style.opacity = '1';
      }, 120);
    }

    thumbBtns.forEach((btn, i) => {
      btn.addEventListener('click', () => setActive(btn));
      btn.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActive(btn); }
        if (e.key === 'ArrowRight') thumbBtns[Math.min(i + 1, thumbBtns.length - 1)].focus();
        if (e.key === 'ArrowLeft')  thumbBtns[Math.max(i - 1, 0)].focus();
      });
    });

    // Transition style
    mainImg.style.transition = 'opacity .12s ease';
  }

  /* ─────────────────────────────────────────
     Share button
  ───────────────────────────────────────── */
  function initShare() {
    const btn = $('#pp-share-btn');
    if (!btn) return;
    btn.addEventListener('click', async () => {
      const data = { title: document.title, url: location.href };
      if (navigator.share && navigator.canShare && navigator.canShare(data)) {
        try { await navigator.share(data); } catch (_) { /* cancelled */ }
      } else {
        await navigator.clipboard.writeText(location.href).catch(() => {});
        const orig = btn.innerHTML;
        btn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>';
        setTimeout(() => { btn.innerHTML = orig; }, 1800);
      }
    });
  }

  /* ─────────────────────────────────────────
     Variant picker
  ───────────────────────────────────────── */
  function initVariants() {
    const variantBtns = $$('.pp-variant-btn');
    if (!variantBtns.length) return;

    const variantsData = window.__ppVariants || [];
    if (!variantsData.length) return;
    const priceCurrentEl = $('#pp-price-current');
    const priceOrigEl    = $('#pp-price-original');
    const saveBadgeEl    = $('#pp-save-badge');
    const previewImg     = $('#pp-variant-preview-img');
    const previewName    = $('#pp-variant-preview-name');
    const mainImg        = $('#pp-main-img');
    const totalPriceEl   = $('#pp-total-price');
    const stickyPriceEl  = $('#pp-sticky-price');

    let currentVariant = null;

    /* Build selectedOptions[] — one entry per option group, seeded from
       the initially active buttons rendered by Liquid.                   */
    const optionGroups   = $$('.pp-variant-btns');
    const selectedOptions = optionGroups.map(group => {
      const active = group.querySelector('.pp-variant-btn.is-active');
      return (active || group.querySelector('.pp-variant-btn'))?.dataset.optionValue || '';
    });

    /* Find the variant whose option1/option2/option3 match selectedOptions */
    function findVariant(opts) {
      return variantsData.find(v =>
        opts.every((val, i) => !val || String(v['option' + (i + 1)]) === String(val))
      ) || null;
    }

    /* formatMoney — used only for dynamic amounts (totals, save badges)
       where Liquid pre-formatting is not possible.
       Reads __ppCurrencySymbol injected by main-product-custom.liquid
       so the symbol is always the correct market currency (€, £, $ etc.)
       instead of the shop default money_format which is always USD.       */
    function formatMoney(cents) {
      if (window.Shopify && window.Shopify.formatMoney) {
        var fmt = window.Shopify.money_format || '${{amount}}';
        var sym = window.__ppCurrencySymbol
               || (window.Shopify.currency && window.Shopify.currency.symbol)
               || '$';
        /* Replace whichever symbol money_format starts with so EUR/GBP etc.
           show correctly when the store default is USD (${{amount}}).      */
        fmt = fmt.replace(/^[$€£¥₹]/, sym);
        return window.Shopify.formatMoney(cents, fmt);
      }
      var fallbackSym = window.__ppCurrencySymbol
                     || (window.Shopify && window.Shopify.currency && window.Shopify.currency.symbol)
                     || '$';
      return fallbackSym + (cents / 100).toFixed(2);
    }

    /* Lookup the Liquid-pre-formatted price strings for a variant id.
       These are injected by main-product-custom.liquid and are guaranteed
       to be in the correct market currency (EUR for Germany, USD for US, etc.) */
    function variantPrices(id) {
      return (window.__ppVariantPrices && window.__ppVariantPrices[String(id)]) || null;
    }

    function updatePrice(variant) {
      if (!variant) return;
      /* Use Liquid-formatted strings for price and compare_at_price.
         This guarantees the correct market currency symbol AND amount. */
      var pre = variantPrices(variant.id);
      if (priceCurrentEl) {
        priceCurrentEl.textContent = (pre && pre.price) ? pre.price : formatMoney(variant.price);
      }
      if (priceOrigEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          priceOrigEl.textContent = (pre && pre.compare_at_price) ? pre.compare_at_price : formatMoney(variant.compare_at_price);
          priceOrigEl.style.display = '';
          if (saveBadgeEl) {
            saveBadgeEl.textContent = 'Save ' + formatMoney(variant.compare_at_price - variant.price);
            saveBadgeEl.style.display = '';
          }
        } else {
          priceOrigEl.style.display = 'none';
          if (saveBadgeEl) saveBadgeEl.style.display = 'none';
        }
      }
      updateTotal();
    }

    function updateTotal() {
      if (!currentVariant || !totalPriceEl) return;
      const qty   = parseInt($('#pp-qty-input')?.value || '1', 10);
      const total = currentVariant.price * qty;
      /* For qty=1, prefer the Liquid-formatted string (exact match).
         For qty>1, use formatMoney — the amount is already in market
         currency cents so only the symbol needs to be correct.          */
      var pre = variantPrices(currentVariant.id);
      var totalFmt = (qty === 1 && pre && pre.price) ? pre.price : formatMoney(total);
      totalPriceEl.textContent = totalFmt;
      if (stickyPriceEl) stickyPriceEl.textContent = totalFmt;
    }

    /* Apply a found variant — update UI, URL, buttons */
    function applyVariant(variant) {
      if (!variant) return;
      currentVariant = variant;

      updatePrice(variant);

      /* Selected text: show all selected values joined */
      if (previewName) {
        previewName.textContent = selectedOptions.filter(Boolean).join(' / ');
      }

      /* Hidden preview img (kept for JS compat) */
      if (previewImg && variant.featured_image?.src) {
        previewImg.src = variant.featured_image.src;
      }

      /* Main gallery image */
      if (mainImg && variant.featured_image?.src) {
        mainImg.style.opacity = '0';
        setTimeout(() => {
          mainImg.src    = variant.featured_image.src;
          mainImg.srcset = '';
          mainImg.style.opacity = '1';
        }, 120);
        $$('.pp-thumb-btn').forEach(t => {
          t.classList.toggle('is-active', t.dataset.imgSrc === variant.featured_image.src);
        });
      }

      /* URL */
      const url = new URL(location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState(null, '', url);

      /* CTA availability — sticky bar disabled state is managed by aplus-content.js */
      const buyBtn  = $('#pp-btn-buy');
      const cartBtn = $('#pp-btn-cart');
      const unavail = !variant.available;
      [buyBtn, cartBtn].forEach(el => { if (el) el.disabled = unavail; });
      if (cartBtn) {
        const textEl = cartBtn.querySelector('.pp-btn-text');
        if (textEl) textEl.textContent = unavail ? 'Sold Out' : 'Add To Cart';
      }
    }

    /* Click handler: update only the clicked group, find combined variant */
    variantBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.classList.contains('is-unavailable')) return;

        const optIdx = parseInt(btn.dataset.optionIndex, 10);
        const optVal = btn.dataset.optionValue;

        /* Update tracked selection for this group */
        selectedOptions[optIdx] = optVal;

        /* Toggle active within THIS group only */
        $$('.pp-variant-btn').forEach(b => {
          if (parseInt(b.dataset.optionIndex, 10) === optIdx) {
            b.classList.remove('is-active');
            b.setAttribute('aria-pressed', 'false');
          }
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', 'true');

        /* Find and apply the combined variant */
        applyVariant(findVariant(selectedOptions));
      });
    });

    /* Init: apply the variant that matches the pre-selected combination */
    applyVariant(findVariant(selectedOptions));

    window.__ppGetCurrentVariant = () => currentVariant;
    window.__ppUpdateTotal        = updateTotal;
  }

  /* ─────────────────────────────────────────
     Quantity selector
  ───────────────────────────────────────── */
  function initQty() {
    const input   = $('#pp-qty-input');
    const minusBtn = $('#pp-qty-minus');
    const plusBtn  = $('#pp-qty-plus');
    if (!input) return;

    function clamp(val) { return Math.max(1, isNaN(val) ? 1 : val); }

    function setQty(val) {
      input.value = clamp(val);
      minusBtn.disabled = parseInt(input.value, 10) <= 1;
      if (window.__ppUpdateTotal) window.__ppUpdateTotal();
    }

    minusBtn?.addEventListener('click', () => setQty(parseInt(input.value, 10) - 1));
    plusBtn?.addEventListener('click',  () => setQty(parseInt(input.value, 10) + 1));
    input.addEventListener('change', () => setQty(parseInt(input.value, 10)));
    input.addEventListener('input',  () => {
      if (input.value === '') return;
      setQty(parseInt(input.value, 10));
    });

    setQty(1); // init
  }

  /* ─────────────────────────────────────────
     Cart count bubble sync
     Directly updates the header bubble count
     using an inline style so it wins over any
     CSS cascade conflict (base.css vs theme).
  ───────────────────────────────────────── */
  function syncCartBubble() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(r => r.json())
      .then(data => {
        const count = data.item_count || 0;

        /* ── Dawn header #cart-icon-bubble ── */
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble) {
          let cb = bubble.querySelector('.cart-count-bubble');
          if (count > 0) {
            if (!cb) {
              cb = document.createElement('div');
              cb.className = 'cart-count-bubble';
              cb.innerHTML = '<span aria-hidden="true"></span><span class="visually-hidden"></span>';
              bubble.appendChild(cb);
            }
            const numEl = cb.querySelector('[aria-hidden]');
            if (numEl) numEl.textContent = count;
            /* Inline style wins over any CSS cascade; ensures visibility
               regardless of base.css / theme.liquid ordering.           */
            cb.style.cssText =
              'display:flex!important;position:absolute;bottom:-2px;right:-8px;' +
              'min-width:18px;height:18px;padding:0 5px;background:#22c55e;' +
              'color:#fff;border-radius:9px;align-items:center;justify-content:center;' +
              'font-size:10px;font-weight:700;pointer-events:none;z-index:2;';
          } else if (cb) {
            cb.style.display = 'none';
          }
        }

        /* ── Custom header .hp-cart-count ── */
        document.querySelectorAll('.hp-cart-count').forEach(el => {
          el.textContent = count;
          el.style.display = count ? '' : 'none';
        });

        /* Also fire the event so theme.liquid listeners stay in sync */
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      })
      .catch(() => {});
  }

  /* ─────────────────────────────────────────
     Add to cart
  ───────────────────────────────────────── */
  function btnLoading(btn, loading) {
    btn.disabled = loading;
    btn.classList.toggle('is-loading', loading);
  }

  async function addToCart(variantId, qty) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: qty })
    });
    if (!res.ok) throw new Error('Cart add failed');
    return res.json();
  }

  function initCartBtn() {
    const btn = $('#pp-btn-cart');
    /* Sticky-bar add button is handled by aplus-content.js via click delegation */

    async function handleAdd(triggerBtn) {
      const variant = window.__ppGetCurrentVariant?.();
      if (!variant) return;
      const qty = parseInt($('#pp-qty-input')?.value || '1', 10);
      btnLoading(triggerBtn, true);
      try {
        await addToCart(variant.id, qty);
        syncCartBubble();
        const textEl = triggerBtn.querySelector('.pp-btn-text');
        const orig = textEl?.textContent;
        if (textEl) textEl.textContent = '✓ Added!';
        setTimeout(() => { if (textEl) textEl.textContent = orig; }, 1800);
      } catch (e) {
        console.error(e);
      } finally {
        btnLoading(triggerBtn, false);
      }
    }

    btn?.addEventListener('click', () => handleAdd(btn));
  }

  /* ─────────────────────────────────────────
     Buy Now
  ───────────────────────────────────────── */
  function initBuyNow() {
    const btn = $('#pp-btn-buy');
    /* Sticky-bar buy button is handled by aplus-content.js via click delegation */

    async function handleBuy(triggerBtn) {
      const variant = window.__ppGetCurrentVariant?.();
      if (!variant) return;
      const qty = parseInt($('#pp-qty-input')?.value || '1', 10);
      btnLoading(triggerBtn, true);
      try {
        await addToCart(variant.id, qty);
        location.href = '/checkout';
      } catch (e) {
        console.error(e);
        btnLoading(triggerBtn, false);
      }
    }

    btn?.addEventListener('click', () => handleBuy(btn));
  }

  /* ─────────────────────────────────────────
     Wishlist (localStorage)
  ───────────────────────────────────────── */
  function initWishlist() {
    const btn = $('#pp-btn-wishlist');
    if (!btn) return;
    const productId = btn.dataset.productId;
    if (!productId) return;

    const KEY = 'pp_wishlist';
    function getList() { try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch { return []; } }
    function saveList(l) { localStorage.setItem(KEY, JSON.stringify(l)); }

    function sync() {
      const wishlisted = getList().includes(productId);
      btn.classList.toggle('is-wishlisted', wishlisted);
      const textEl = btn.querySelector('.pp-btn-text');
      if (textEl) textEl.textContent = wishlisted ? '♥ Wishlisted' : '♡ Wishlist';
    }

    btn.addEventListener('click', () => {
      const list = getList();
      const idx = list.indexOf(productId);
      if (idx === -1) list.push(productId); else list.splice(idx, 1);
      saveList(list);
      sync();
    });

    sync();
  }

  /* ─────────────────────────────────────────
     A+ Content — image card wrapping + lazy load
  ───────────────────────────────────────── */
  function initAplusContent() {
    const content = document.querySelector('.pp-aplus-content');
    if (!content) return;

    content.querySelectorAll('img').forEach(function (img) {
      /* Add lazy loading */
      if (!img.hasAttribute('loading')) img.setAttribute('loading', 'lazy');

      /* Skip if already wrapped */
      if (img.parentElement && img.parentElement.classList.contains('pp-aplus-img-card')) return;

      const card = document.createElement('div');
      card.className = 'pp-aplus-img-card';

      /* If img is the sole child of a <p>, replace the <p> with the card */
      const parent = img.parentElement;
      if (parent && parent.tagName === 'P' && parent.children.length === 1 && parent.textContent.trim() === img.alt.trim()) {
        parent.replaceWith(card);
      } else if (parent && parent.tagName === 'P' && parent.childNodes.length === 1) {
        parent.replaceWith(card);
      } else {
        img.parentElement.insertBefore(card, img);
      }
      card.appendChild(img);
    });
  }

  /* ─────────────────────────────────────────
     Init all
  ───────────────────────────────────────── */
  function initAll() {
    initGallery();
    initShare();
    initVariants();
    initQty();
    initCartBtn();
    initBuyNow();
    initWishlist();
    initAplusContent();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* Re-init variants if the inline __ppVariants script fires after this
     file (e.g. Theme Editor section reload). Only re-runs initVariants
     since gallery/qty/cart are DOM-stable across section reloads.     */
  document.addEventListener('ppvariants:ready', initVariants);

})();
